import React, { useEffect } from "react";
import PageLayout from "../../layouts/PageLayout";
import { AndonHeroAnimation } from "../../components/SolutionsHeroAnimations";
import "../../layouts/PageLayout.css";
import "./DigitalAndon.css";

export default function DigitalAndon() {
  useEffect(() => {
    // SEO logic
    document.title = "Digital Andon System - PiTech Automation";
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://www.pitechautomation.com/industry/digital-andon');

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
        metaDesc.setAttribute("content", "Real-time Digital Andon System by PiTech Automation. Visualize production status, respond to issues instantly, and eliminate downtime.");
    }

    const observer = new IntersectionObserver(
      entries =>
        entries.forEach(e =>
          e.isIntersecting && e.target.classList.add("visible")
        ),
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".animate-on-scroll").forEach(el =>
      observer.observe(el)
    );
    return () => observer.disconnect();
  }, []);

  return (
    <PageLayout
      title="Digital <span>Andon System</span>"
      subtitle="Visualize production status in real-time, respond to issues instantly, and eliminate downtime with intelligent alert escalation."
      tag="Production Monitoring"
      heroImage="/images/digital-andonnn.jpg"
      heroAnimation={<AndonHeroAnimation />}
      breadcrumb={[
        { label: "Solutions", path: "/services" },
        { label: "Digital Andon" }
      ]}
    >
      {/* Section 1: What is Digital Andon? */}
      <section style={{ padding: "60px 20px 40px", backgroundColor: "var(--white)" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {/* Section Header */}
          <div className="animate-on-scroll" style={{ marginBottom: "36px" }}>
            <h2
              style={{
                fontSize: "32px",
                fontWeight: "700",
                marginBottom: "16px",
                color: "var(--gray-900)"
              }}
            >
              Real-time Visual Control
            </h2>
            <p
              style={{
                fontSize: "16px",
                lineHeight: "1.6",
                color: "var(--gray-600)",
                maxWidth: "850px"
              }}
            >
              The Digital Andon System replaces manual alert systems with intelligent, real-time visualization that ensures no production issue goes unnoticed. Automatically escalate alerts through your team hierarchy and track response times for continuous improvement.
            </p>
          </div>

          <div className="animate-on-scroll realtime-control-grid">
            {/* Left Column - 2-Column Grid of 6 Points */}
            <div className="realtime-points-grid">
              {[
                {
                  title: "Real-time Status Display",
                  desc: "Live production line visualization on large displays"
                },
                {
                  title: "Automatic Alert Escalation",
                  desc: "Multi-level escalation: operator → supervisor → manager"
                },
                {
                  title: "Machine Status Monitoring",
                  desc: "Green/Yellow/Red status with timestamps"
                },
                {
                  title: "Response Time Tracking",
                  desc: "Measure team response time for every alert"
                },
                {
                  title: "Shift Reports",
                  desc: "Automated end-of-shift performance summaries"
                },
                {
                  title: "Mobile Notifications",
                  desc: "SMS/WhatsApp alerts to on-call engineers"
                }
              ].map((feature, idx) => (
                <div key={idx} className="realtime-point-card">
                  <div className="realtime-point-icon">
                    ✓
                  </div>
                  <div>
                    <h4
                      style={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "var(--gray-900)",
                        marginBottom: "4px",
                        lineHeight: "1.3"
                      }}
                    >
                      {feature.title}
                    </h4>
                    <p style={{ fontSize: "12px", color: "var(--gray-600)", margin: 0, lineHeight: "1.45" }}>
                      {feature.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Right Column - Image */}
            <div className="realtime-image-wrap">
              <img
                src="/images/andon digital.jpeg"
                alt="Digital Andon System"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Key Benefits Grid */}
      <section
        className="benefits-section"
        style={{ padding: "50px 20px 60px", backgroundColor: "var(--gray-50)" }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "36px" }}>
            <h2
              style={{
                fontSize: "32px",
                fontWeight: "700",
                color: "var(--gray-900)",
                marginBottom: "16px"
              }}
            >
              Why Digital Andon
            </h2>
            <p
              style={{
                fontSize: "16px",
                color: "var(--gray-600)",
                maxWidth: "600px",
                margin: "0 auto"
              }}
            >
              Faster response times, complete visibility, and data-driven decision making across your entire facility.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "30px"
            }}
          >
            {[
              {
                metric: "87%",
                label: "Faster Response",
                desc: "Instant notification triggers alert maintenance and support teams immediately upon stoppage."
              },
              {
                metric: "Zero",
                label: "Missed Alerts",
                desc: "Multi-tiered automatic escalation ensures critical line alerts never go unaddressed."
              },
              {
                metric: "Visual",
                label: "Factory Control",
                desc: "Color-coded shop floor dashboards offer complete visibility across all operating machines."
              },
              {
                metric: "Live",
                label: "Shift Analytics",
                desc: "Continuously record response times, breakdown root causes, and shift-level productivity."
              },
              {
                metric: "Mobile",
                label: "Ready Alerts",
                desc: "Send instant SMS, WhatsApp, and push notifications to engineers wherever they are located."
              },
              {
                metric: "Custom",
                label: "Escalation Rules",
                desc: "Easily configure response thresholds, supervisor tiers, and department routing rules."
              }
            ].map((benefit, idx) => (
              <div
                key={idx}
                className="animate-on-scroll"
                style={{
                  backgroundColor: "var(--white)",
                  padding: "36px 24px",
                  borderRadius: "var(--radius-lg)",
                  boxShadow: "var(--shadow)",
                  border: "1px solid var(--gray-100)",
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  transition: "all 0.3s ease"
                }}
              >
                <div
                  style={{
                    fontSize: "32px",
                    fontWeight: "700",
                    color: "var(--primary-light)",
                    marginBottom: "10px"
                  }}
                >
                  {benefit.metric}
                </div>
                <h4
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "var(--gray-900)",
                    marginBottom: "10px"
                  }}
                >
                  {benefit.label}
                </h4>
                <p
                  style={{
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "var(--gray-600)",
                    margin: 0
                  }}
                >
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: How It Works */}
      <section style={{ padding: "80px 20px", backgroundColor: "var(--white)" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <h2
              style={{
                fontSize: "32px",
                fontWeight: "700",
                color: "var(--gray-900)",
                marginBottom: "16px"
              }}
            >
              Alert Escalation Workflow
            </h2>
            <p
              style={{
                fontSize: "16px",
                color: "var(--gray-600)",
                maxWidth: "600px",
                margin: "0 auto"
              }}
            >
              Intelligent routing ensures the right person gets notified at the right time.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "30px"
            }}
          >
            {[
              {
                level: "Level 1",
                role: "Operator",
                action: "Receives instant notification",
                time: "Immediate"
              },
              {
                level: "Level 2",
                role: "Supervisor",
                action: "Escalation if no response",
                time: "5-10 minutes"
              },
              {
                level: "Level 3",
                role: "Manager",
                action: "Further escalation if needed",
                time: "15-20 minutes"
              },
              {
                level: "Level 4",
                role: "Director",
                action: "Critical alert notification",
                time: "Immediate"
              }
            ].map((item, idx) => (
              <div
                key={idx}
                className="animate-on-scroll"
                style={{
                  position: "relative",
                  padding: "40px 30px",
                  backgroundColor: "var(--gray-50)",
                  borderRadius: "var(--radius-lg)",
                  borderTop: "4px solid var(--primary-light)"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px"
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "700",
                      color: "var(--primary-light)",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}
                  >
                    {item.level}
                  </span>
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "var(--gray-900)"
                    }}
                  >
                    {item.role}
                  </h3>
                  <p style={{ fontSize: "14px", color: "var(--gray-600)", marginBottom: "12px" }}>
                    {item.action}
                  </p>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "var(--primary-light)",
                      paddingTop: "12px",
                      borderTop: "1px solid var(--gray-200)"
                    }}
                  >
                    {item.time}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: Integration & Mobile */}
      <section style={{ padding: "80px 20px", backgroundColor: "var(--gray-50)" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "60px",
              alignItems: "center"
            }}
          >
            {/* Left Column - Text */}
            <div className="animate-on-scroll">
              <h2
                style={{
                  fontSize: "32px",
                  fontWeight: "700",
                  marginBottom: "20px",
                  color: "var(--gray-900)"
                }}
              >
                Mobile & Integration
              </h2>
              <p
                style={{
                  fontSize: "16px",
                  lineHeight: "1.6",
                  color: "var(--gray-600)",
                  marginBottom: "30px"
                }}
              >
                Digital Andon works seamlessly across all devices and integrates with your existing MES, ERP, and communication platforms.
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                {[
                  {
                    icon: "📱",
                    title: "Mobile Alerts",
                    desc: "Push notifications, SMS, WhatsApp, and Telegram"
                  },
                  {
                    icon: "🖥️",
                    title: "Large Displays",
                    desc: "Real-time status boards for shop floor visibility"
                  },
                  {
                    icon: "🔗",
                    title: "System Integration",
                    desc: "API integration with MES, ERP, and IoT platforms"
                  }
                ].map((item, idx) => (
                  <div key={idx} style={{ display: "flex", gap: "16px" }}>
                    <div
                      style={{
                        fontSize: "28px",
                        flexShrink: 0
                      }}
                    >
                      {item.icon}
                    </div>
                    <div>
                      <h4
                        style={{
                          fontWeight: "600",
                          color: "var(--gray-900)",
                          marginBottom: "4px"
                        }}
                      >
                        {item.title}
                      </h4>
                      <p style={{ fontSize: "14px", color: "var(--gray-600)" }}>
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Image */}
            <div
              className="animate-on-scroll"
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: "400px",
                  borderRadius: "var(--radius-lg)",
                  overflow: "hidden",
                  boxShadow: "var(--shadow-lg)",
                  border: "1px solid var(--gray-200)",
                  backgroundColor: "var(--white)",
                  padding: "14px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                <img
                  src="/images/andon_mobile_image.jpg"
                  alt="Mobile Andon Interface"
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "400px",
                    objectFit: "contain",
                    borderRadius: "var(--radius-sm)",
                    display: "block"
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>


    </PageLayout>
  );
}
